import { getInputFindSheetErrorMessage } from '../../policies/input-find-sheet-error-policy.mjs'

const clone = value => JSON.parse(JSON.stringify(value))


export const ISSUE_TYPES = ['워크플로우 실행 오류', '워크플로우 결과 이상', '파일·데이터 관련', '계정·권한', '결제·계약', '기능 문의·개선 요청', '기타']
export const ISSUE_STATUSES = ['접수', '확인 중', '고객 답변 대기', '조치 중', '해결 확인 대기', '종료']
export const ISSUE_PRIORITIES = ['낮음', '보통', '높음', '긴급']

const ISSUE_STATUS_TONES = {
  접수: 'gray',
  '확인 중': 'blue',
  '고객 답변 대기': 'orange',
  '조치 중': 'red',
  '해결 확인 대기': 'purple',
  종료: 'green',
}

export function getCSIssueStatusTone(status) {
  return ISSUE_STATUS_TONES[status] || 'gray'
}

export function normalizeCSIssueOriginPath(path) {
  if (typeof path !== 'string') return null
  if (path !== path.trim()) return null
  const normalized = path
  if (!/^\/(?!\/)/.test(normalized)) return null
  if (normalized.includes('\\')) return null
  if ([...normalized].some(character => {
    const code = character.charCodeAt(0)
    return code < 32 || (code >= 127 && code <= 159)
  })) return null
  return normalized
}

export function normalizeCSIssueBlockFocusId(blockId) {
  if (typeof blockId !== 'string' && typeof blockId !== 'number') return null
  const normalized = String(blockId)
  if (normalized !== normalized.trim()) return null
  return /^[A-Za-z0-9_-]{1,128}$/.test(normalized) ? normalized : null
}

export function buildCSWorkflowDesignPath(projectId, workflowId) {
  const normalizedProjectId = normalizeCSIssueBlockFocusId(projectId)
  const normalizedWorkflowId = normalizeCSIssueBlockFocusId(workflowId)
  if (!normalizedProjectId || !normalizedWorkflowId) return null
  return `/rpa/${normalizedProjectId}/${normalizedWorkflowId}?panel=design`
}

function isCSWorkflowDesignPath(path) {
  if (!path) return false
  const target = new URL(path, 'https://recolumn.local')
  const segments = target.pathname.split('/').filter(Boolean)
  return ['rpa', 'rpa3'].includes(segments[0])
    && segments.length === 3
    && normalizeCSIssueBlockFocusId(segments[1]) === segments[1]
    && normalizeCSIssueBlockFocusId(segments[2]) === segments[2]
}

export function buildCSIssueOriginTarget(path, primaryBlockId, context = {}) {
  let normalizedPath = normalizeCSIssueOriginPath(path)
  const isWorkflowRun = ['workflow-error', 'workflow_error', 'menu'].includes(context.source)
  const isProjectNavbar = ['project-navbar', 'project_navbar'].includes(context.source)
  if ((isWorkflowRun && !isCSWorkflowDesignPath(normalizedPath)) || (isProjectNavbar && !normalizedPath)) {
    normalizedPath = buildCSWorkflowDesignPath(context.projectId, context.workflowId)
  }
  if (!normalizedPath) return null
  const blockId = normalizeCSIssueBlockFocusId(primaryBlockId)
  if (!blockId) return normalizedPath
  const target = new URL(normalizedPath, 'https://recolumn.local')
  target.searchParams.set('csBlockId', blockId)
  return `${target.pathname}${target.search}${target.hash}`
}

export function getCSIssueSourceLabel(context = {}) {
  if (context.source === 'project-navbar') {
    const workflowDesignRoutes = ['RpaMain', 'RpaMain3']
    const isWorkflowDesign = workflowDesignRoutes.includes(context.originScreen?.routeName)
      || context.originScreen?.name === '워크플로우 설계'
    return isWorkflowDesign ? '워크플로우 설계창' : '프로젝트 상단바'
  }

  return ({
    menu: 'CS 메뉴 직접 등록',
    'data-result': '데이터 결과 파일',
    'workflow-result': '워크플로우 결과 화면',
    'workflow-error': '워크플로우 실행창',
  })[context.source] || context.source
}

export function isContextualWorkflowEntry(context = {}) {
  if (['workflow-error', 'workflow-result'].includes(context.source)) return true
  if (context.source !== 'project-navbar' || !context.workflow) return false
  return ['RpaMain', 'RpaMain3'].includes(context.originScreen?.routeName)
    || ['워크플로우 설계', '워크플로우 설계창'].includes(context.originScreen?.name)
}

export function getLatestCSExecution(executions = []) {
  return Array.isArray(executions) && executions.length > 0 ? clone(executions[0]) : null
}

export function getContextualCSExecution(capturedExecution, executions = []) {
  if (capturedExecution?.id != null) {
    const loadedExecution = executions.find(execution => String(execution?.id) === String(capturedExecution.id))
    return loadedExecution ? clone(loadedExecution) : null
  }
  return getLatestCSExecution(executions)
}

export function getCSExecutionOutputs(execution) {
  return Array.isArray(execution?.outputs) ? clone(execution.outputs) : []
}

const CS_WORKFLOW_RUN_ERROR_KEYS = [
  'result', 'success', 'fail', 'fail_type', 'error', 'timestamp', 'time_log', 'is_thread',
  'err_code', 'err_msg', 'message', 'detail', 'activity_id', 'failed_activity_id',
]

export function normalizeCSWorkflowRunErrorResponse(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  const normalized = {}
  CS_WORKFLOW_RUN_ERROR_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] !== undefined) {
      normalized[key] = clone(payload[key])
    }
  })
  return Object.keys(normalized).length ? normalized : null
}

export function getCSWorkflowRunErrorMessage(payload) {
  const mappingMessage = getInputFindSheetErrorMessage(payload)
  if (mappingMessage) return mappingMessage
  if (payload == null) return ''
  if (typeof payload === 'string') return payload
  if (typeof payload !== 'object') return String(payload)
  const error = payload.error
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const nested = error.message ?? error.err_msg ?? error.detail ?? error.error
    if (nested != null && nested !== error) return getCSWorkflowRunErrorMessage(nested)
    try { return JSON.stringify(error) } catch (serializationError) { return String(error) }
  }
  const direct = payload.message ?? payload.err_msg ?? payload.detail
  return direct == null ? '' : getCSWorkflowRunErrorMessage(direct)
}

function getCSWorkflowRunFailureId(payload = {}) {
  const failure = payload.fail ?? payload.failed_activity_id ?? payload.activity_id
  if (failure && typeof failure === 'object') {
    return failure.id ?? failure.activity_id ?? failure.failed_activity_id ?? failure.fail ?? null
  }
  return failure ?? null
}

export function buildCSWorkflowRunFailureBlock(blocks = [], payload = {}) {
  const failureId = getCSWorkflowRunFailureId(payload)
  const matchedBlock = failureId == null
    ? null
    : (blocks || []).find(block => String(block?.id) === String(failureId))
  const sourceBlock = matchedBlock
  if (!sourceBlock) return null
  return {
    ...clone(sourceBlock),
    status: 'failed',
    run_status: 'fail',
    error: getCSWorkflowRunErrorMessage(payload) || sourceBlock.error,
  }
}

export function resolveCSDiagnosticBlock(primaryBlock, designBlocks = [], workflowRunError = null) {
  if (!primaryBlock) return null
  const designBlock = (designBlocks || []).find(block => String(block?.id) === String(primaryBlock.id))
  const failureId = getCSWorkflowRunFailureId(workflowRunError || {})
  const isCapturedFailure = primaryBlock.run_status === 'fail'
    || primaryBlock.status === 'failed'
    || (failureId != null && String(failureId) === String(primaryBlock.id))
  const resolved = {
    ...(designBlock ? clone(designBlock) : {}),
    ...clone(primaryBlock),
    error: primaryBlock.error || designBlock?.error,
  }
  if (isCapturedFailure) {
    resolved.status = 'failed'
    resolved.run_status = 'fail'
  }
  return resolved
}

const normalizedEntity = entity => entity ? clone(entity) : null

export function toCSIssueBlockContext(block) {
  if (!block) return null
  const safeBlock = {}
  const keys = ['id', 'name', 'edited_name', 'type', 'category', 'status', 'run_status', 'error']
  keys.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(block, key) && block[key] !== undefined) safeBlock[key] = clone(block[key])
  })
  return safeBlock
}

export function toCSIssueExecutionContext(execution) {
  if (!execution) return null
  const safeExecution = {}
  const keys = [
    'id', 'issue_number', 'status', 'started_at', 'startedAt', 'finished_at', 'ended_at',
    'running_time', 'duration', 'error', 'diagnostic_failed', 'workflow_id', 'rpa_id',
    'version', 'version_minor', 'failed_activity_id', 'activity_id', 'fail', 'inputs', 'outputs',
  ]
  keys.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(execution, key) && execution[key] !== undefined) safeExecution[key] = clone(execution[key])
  })
  return Object.keys(safeExecution).length > 0 ? safeExecution : null
}

export function toCSExecutionResultFile(output, executionFinishedAt = null) {
  if (!output) return null
  const source = clone(output)
  const isDeleted = Boolean(output.is_deleted || output.state === 'deleted')
  return {
    id: output.id ?? output.target_id ?? output.rpa_output_id ?? null,
    name: output.name || output.rpa_output_name || '결과 파일',
    size: output.size ?? null,
    sheetCount: output.table_cnt ?? output.sheet_count ?? null,
    category: output.category || 'file',
    executionFinishedAt,
    isDeleted,
    disabled: isDeleted,
    state: isDeleted ? 'deleted' : (output.state || 'ready'),
    deletedMessage: output.deleted_message || '',
    source,
  }
}

export function normalizeResultFiles(payload = {}) {
  if (Array.isArray(payload.resultFiles)) return clone(payload.resultFiles.filter(Boolean))
  return payload.resultFile ? [clone(payload.resultFile)] : []
}

export function normalizeExecutionSelection(source, execution) {
  return source === 'project-navbar' ? toCSIssueExecutionContext(execution) : normalizedEntity(execution)
}

export function resolveDraftLinkedContext(captured = {}, loaded = {}) {
  const findById = (items, entity) => {
    if (entity?.id == null) return null
    return (items || []).find(item => String(item?.id) === String(entity.id)) || null
  }
  const loadedPrimaryBlock = findById(loaded.blocks, captured.primaryBlock)
  const primaryBlock = loadedPrimaryBlock
    ? {
      ...loadedPrimaryBlock,
      ...(captured.primaryBlock?.error != null ? { error: clone(captured.primaryBlock.error) } : {}),
      ...(captured.primaryBlock?.status != null ? { status: captured.primaryBlock.status } : {}),
      ...(captured.primaryBlock?.run_status != null ? { run_status: captured.primaryBlock.run_status } : {}),
    }
    : null
  return {
    execution: normalizedEntity(findById(loaded.executions, captured.execution)),
    primaryBlock: normalizedEntity(primaryBlock),
    relatedBlocks: (captured.relatedBlocks || []).map(block => normalizedEntity(findById(loaded.blocks, block))).filter(Boolean),
  }
}

export function selectProjectNavbarBlock(primaryBlock, block) {
  if (primaryBlock && String(primaryBlock.id) === String(block?.id)) return null
  return toCSIssueBlockContext(block)
}

export function normalizeDraftContext(source = 'menu', payload = {}) {
  const sourceType = {
    'workflow-error': '워크플로우 실행 오류',
    'workflow-result': '워크플로우 결과 이상',
    'data-result': '워크플로우 결과 이상',
  }
  const shouldSanitizeBlocks = source === 'project-navbar'
  const primaryBlock = shouldSanitizeBlocks
    ? toCSIssueBlockContext(payload.primaryBlock)
    : normalizedEntity(payload.primaryBlock)
  if (primaryBlock && source === 'workflow-error') {
    primaryBlock.status = 'failed'
    primaryBlock.run_status = 'fail'
  } else if (primaryBlock && source !== 'project-navbar' && primaryBlock.run_status === 'fail') {
    primaryBlock.status = primaryBlock.status || 'failed'
  }
  return {
    source,
    type: sourceType[source] || payload.type || '기타',
    project: normalizedEntity(payload.project),
    originScreen: normalizedEntity(payload.originScreen),
    workflow: normalizedEntity(payload.workflow),
    execution: normalizeExecutionSelection(source, payload.execution),
    primaryBlock,
    relatedBlocks: shouldSanitizeBlocks
      ? (payload.relatedBlocks || []).map(toCSIssueBlockContext).filter(Boolean)
      : clone(payload.relatedBlocks || []),
    resultFile: normalizedEntity(payload.resultFile),
    resultFiles: normalizeResultFiles(payload),
    workflowRunError: normalizeCSWorkflowRunErrorResponse(payload.workflowRunError),
  }
}

export function createDraft(state, context) {
  const id = `draft-${state.nextDraftId++}`
  state.drafts[id] = clone(context)
  return id
}

export function getDraft(state, id) {
  return state.drafts[id] ? clone(state.drafts[id]) : null
}
